import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangleIcon, InfoIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/src/utils/styles'
import { Button } from './button'

const bannerVariants = cva('flex gap-5 p-5', {
  variants: {
    variant: {
      informative: 'bg-surface2',
      warning: 'bg-danger',
    },
    placement: {
      block: '',
      inline: 'bg-transparent rounded-[20px] outline outline-1 outline-border2',
    },
  },
  defaultVariants: {
    variant: 'informative',
    placement: 'block',
  },
})

interface CommonPrimaryActionAttributes {
  label: string
}
interface ButtonPrimaryAction {
  onClick: () => void
}
interface LinkPrimaryAction {
  href: string
}
type PrimaryAction = CommonPrimaryActionAttributes &
  (ButtonPrimaryAction | LinkPrimaryAction)

interface PrimaryActionTextProps {
  primaryAction: PrimaryAction
}

const PrimaryActionText = ({ primaryAction }: PrimaryActionTextProps) => {
  return (
    <span className='text-link text-sm font-[420]'>{primaryAction.label}</span>
  )
}

interface BannerProps
  extends ComponentProps<'div'>,
    VariantProps<typeof bannerVariants> {
  alertTitle?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  onClose?: () => void
  primaryAction?: PrimaryAction
  hideIcon?: boolean
}

const Banner = ({
  className,
  alertTitle,
  description,
  icon,
  variant = 'informative',
  placement,
  onClose,
  primaryAction,
  hideIcon,
  ref,
  ...props
}: BannerProps) => {
  const defaultIcon =
    variant === 'informative' ? (
      <InfoIcon width={24} height={24} className='text-tertiary shrink-0' />
    ) : (
      <AlertTriangleIcon
        width={24}
        height={24}
        className='text-danger-text shrink-0'
      />
    )

  const isFooterVisible = onClose !== undefined || primaryAction !== undefined
  return (
    <div
      ref={ref}
      className={cn(bannerVariants({ variant, placement, className }))}
      {...props}
    >
      {!hideIcon && (icon || defaultIcon)}
      <div className='flex flex-row flex-wrap justify-between gap-4 w-full'>
        <div className='flex flex-col gap-1 py-0.5'>
          <h6
            className={cn(
              'text-sm font-semibold',
              variant === 'warning' ? 'text-danger-text' : 'text-primary',
            )}
          >
            {alertTitle}
          </h6>
          <p className='text-sm text-secondary font-[420]'>{description}</p>
        </div>
        {isFooterVisible && (
          <div className='flex items-center justify-end flex-1 self-end gap-8'>
            {onClose && (
              <Button type='button' onClick={onClose} size='sm'>
                <span className='text-link text-sm font-[420]'>Dismiss</span>
              </Button>
            )}
            {primaryAction && 'onClick' in primaryAction && (
              <Button type='button' onClick={primaryAction.onClick} size='sm'>
                <PrimaryActionText primaryAction={primaryAction} />
              </Button>
            )}
            {primaryAction && 'href' in primaryAction && (
              <a href={primaryAction.href}>
                <Button type='button' asChild size='sm'>
                  <PrimaryActionText primaryAction={primaryAction} />
                </Button>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

Banner.displayName = 'Banner'

export { Banner }
